import { BucketPayload } from '../../../../../../store/dashboard/financials/entityRationaleServices';

export interface Citation {
    doc_type: string | null;
    source: string | null;
    doc_name: string | null;
}

export interface EntityRationale {
    rationale: string | null;
    type: string;
    entityRationale: string;
    citations: Citation[];
    entityId: number;
    /** Added in parallel contract update — absent on old backend responses. */
    classificationId?: number | null;
    /** True when the entity's rationale was written by a user edit; false/absent for AI (Ask-AICA) rationales. */
    rationaleIsManual?: boolean;
    /** 1 when a grain was user-cleared (frozen + bt.rationale IS NULL); 0 otherwise. Absent on old-backend responses. */
    rationaleCleared?: number;
}

export interface BankEntityRationaleResponse {
    entities: EntityRationale[];
}

export interface BankTransaction {
    id: string;
    date: string;
    amount: number | string;
    transactionType: string;
    transactionNote: string;
    taggedEntities: string[];
}

export type BucketType = 'rationale' | 'entity' | 'other';

export interface CashflowSubRow {
    name: string;
    bucketType: BucketType;
    bucketKey?: string | null;
    entityId?: number;
    classificationId?: number | null;
    source?: string | null;
    /** Server-set flag: true when this row has been edited via editClassifications. */
    edited?: boolean;
    amounts?: Array<{ period: string; amount: string | number }>;
    amountsMap?: Record<string, string | number>;
}

export interface HoverPayload {
    entityRationale: string;
    primaryDocName: string | null;
    allCitations: Array<{ doc_type: string | null; doc_name: string | null }>;
}

export interface ChipInfo {
    label: string;
    isLender: boolean;
    hoverPayload: HoverPayload;
    entityId: number;
}

/** Canonical grain key: one null representation used by both staging and render. */
export function grainKey(classificationId: number | null | undefined, rationale: string | null | undefined): string {
    return `${classificationId ?? 'null'}|${rationale ?? ''}`;
}

/** Per-grain staged edit — one entry per (currentClassificationId, currentRationale) grain. Covers all op types. */
export interface GrainEdit {
    /** Row's current rationale at stage time: parsed bucketKey for kind-1, null for kind-2 residue. */
    currentRationale: string | null;
    /** Row's current classificationId at stage time. */
    currentClassificationId: number | null;
    rationale?: string;
    clearRationale?: boolean;
    classificationId?: number;
    clearClassification?: boolean;
    unlink?: boolean;
    targetEntityId?: number;
    targetEntityName?: string;
    entityName?: string;
}

/** One pending edit for a single entity, held in the staged-edits map before Save. */
export interface StagedEdit {
    currentClassificationId: number;
    /** Per-grain edits keyed by grainKey(currentClassificationId, currentRationale). All op types live here after D30. */
    grainEdits?: Map<string, GrainEdit>;
    // Routing-only: present in patches to handleStage, never stored at entity level.
    currentRationale?: string | null;
    rationale?: string;
    clearRationale?: boolean;
    classificationId?: number;
    clearClassification?: boolean;
    entityName?: string;
    unlink?: boolean;
    targetEntityId?: number;
    targetEntityName?: string;
}

export interface DrawerState {
    entityName: string;
    /** Bucket request that opened the drawer — retained so a post-save edit can refetch the same bucket. */
    payload: BucketPayload;
    transactions: BankTransaction[];
    loading: boolean;
}

/** Display unit for cashflow amounts. */
export type CashflowUnit = 'INR' | 'Lakhs' | 'Crore';
